import { useCallback, useEffect, useState } from "react";
import { Target, Pencil, Archive, RotateCcw } from "lucide-react";
import { toast } from "sonner";

import { AppShell } from "@/components/layout/AppShell";
import { PageHeader } from "@/components/common/PageHeader";
import { LoadingState, EmptyState } from "@/components/common/StateBlocks";
import { GoalDialog } from "@/components/goals/GoalDialog";
import { growService } from "@/services/growService";
import { goalTypeLabel, isMoneyGoal, paceColor } from "@/config/growOptions";
import { fmtCents } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";

const fmtVal = (type, v) => (isMoneyGoal(type) ? fmtCents(v) : String(v ?? 0));

export default function Goals() {
  const [tab, setTab] = useState("active");
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [editGoal, setEditGoal] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setGoals(await growService.listGoals(tab)); } catch { toast.error("Could not load goals"); }
    finally { setLoading(false); }
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  const archive = async (g) => { try { await growService.archiveGoal(g.id); toast.success("Goal archived"); load(); } catch { toast.error("Could not archive"); } };
  const restore = async (g) => { try { await growService.restoreGoal(g.id); toast.success("Goal restored"); load(); } catch { toast.error("Could not restore"); } };

  return (
    <AppShell>
      <div className="space-y-6" data-testid="goals-page">
        <PageHeader eyebrow="AgentHQ · Grow" title="Goals"
          description="Set the target. AgentHQ tracks the progress from your real business data."
          actions={<Button onClick={() => { setEditGoal(null); setDialog(true); }} data-testid="goal-new"><Target className="h-4 w-4" /> New Goal</Button>} />

        <div className="flex gap-1 border-b border-border">
          {["active", "archived"].map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`goals-tab-${t}`}
              className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium capitalize transition-colors ${tab === t ? "border-brand-gold text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>{t}</button>
          ))}
        </div>

        {loading ? <div className="py-10"><LoadingState label="Loading goals" /></div>
          : goals.length === 0 ? (
            <EmptyState title="Set the target. AgentHQ will track the progress."
              description="Create an income, sales-volume, transaction, listing or lead goal to get started."
              action={tab === "active" && <Button className="mt-5" onClick={() => { setEditGoal(null); setDialog(true); }} data-testid="goal-empty-new"><Target className="h-4 w-4" /> New Goal</Button>} />
          ) : (
            <div className="grid gap-4 md:grid-cols-2" data-testid="goals-list">
              {goals.map((g) => (
                <Card key={g.id} data-testid={`goal-card-${g.id}`}>
                  <CardContent className="space-y-3 p-5">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-heading text-lg font-semibold text-foreground">{goalTypeLabel(g.goal_type)}</p>
                        <p className="text-xs text-muted-foreground capitalize">{g.period_type} · {(g.start_date||"").slice(0,10)} → {(g.end_date||"").slice(0,10)}</p>
                      </div>
                      <div className="flex gap-1">
                        {tab === "active" ? (
                          <>
                            <button onClick={() => { setEditGoal(g); setDialog(true); }} className="rounded-md border border-border p-1.5 hover:bg-secondary" data-testid={`goal-edit-${g.id}`}><Pencil className="h-4 w-4" /></button>
                            <button onClick={() => archive(g)} className="rounded-md border border-border p-1.5 text-muted-foreground hover:bg-secondary" data-testid={`goal-archive-${g.id}`}><Archive className="h-4 w-4" /></button>
                          </>
                        ) : (
                          <button onClick={() => restore(g)} className="rounded-md border border-border p-1.5 hover:bg-secondary" data-testid={`goal-restore-${g.id}`}><RotateCcw className="h-4 w-4" /></button>
                        )}
                      </div>
                    </div>
                    <Progress value={g.progress_percent} className="h-2.5" data-testid={`goal-progress-${g.id}`} />
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium text-foreground">{fmtVal(g.goal_type, g.current_value)} <span className="text-muted-foreground">of {fmtVal(g.goal_type, g.target_value)}</span></span>
                      <span className="font-semibold">{g.progress_percent}%</span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">{fmtVal(g.goal_type, g.remaining)} to go</span>
                      <Badge variant="outline" className={`capitalize ${paceColor(g.pace)}`}>{g.pace.replace("_", " ")}</Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
      </div>
      <GoalDialog open={dialog} onOpenChange={setDialog} goal={editGoal} onSaved={load} />
    </AppShell>
  );
}
